import { Localized } from '../../internal/Localized';
import Styles from './Landing.module.css';
import { Button, Label } from '@app/engine';
import { CommonConstants, FormatCurrencyMinor, FormatLocalized, GetCurrencySymbol, IPlanDTO, ResolveCurrencyAmount, ResolveSupportedCurrency } from '@shared/common';
import { useMemo } from 'react';
import type { BillingCurrency } from '@shared/common';
import type { KeyboardEvent } from 'react';

/** Props for the PricingCard. */
interface IPricingCardProps {
  plans: IPlanDTO[];
  // Currency the visitor's prices are shown in (resolved from geo/tenant country upstream).
  currency: BillingCurrency;
  onChoosePlan: (planCode: string) => void;
}

/** A single pricing plan definition. */
interface IPlan {
  key: string;
  name: string;
  tagline: string;
  // Currency symbol for the plan's effective currency (e.g. "$").
  currencySymbol: string;
  // Uppercased effective currency code shown in the note line (e.g. "USD").
  currencyCode: string;
  price: string;
  // True when the plan's recurring base fee resolves to zero in the effective currency; the amount slot renders FREE instead of a number.
  free: boolean;
  setupPrice: string;
  // True when the setup fee resolves to zero in the effective currency; the "+ setup" clause is dropped from the price suffix.
  setupFree: boolean;
  features: string[];
  cta: string;
  featured: boolean;
}

/** File-local constants for PricingCard (decorative glyph, currency divisor). */
const localConstants = {
  check: '✓',
  minorUnitsPerMajor: 100,
  firstIndex: 0,
  keyEnter: 'Enter',
  keySpace: ' '
} as const;

/**
 * Builds the four fixed pricing/concurrency bullets that are appended to every plan card.
 *
 * @param plan - The source plan DTO.
 * @param effectiveCurrency - The currency the visitor's prices are shown in.
 * @param concurrentLimitOverride - Optional tenant-specific concurrent-line cap (the Portal per-tenant override); when set, it replaces the plan's value so the tenant's own dashboard shows their
 *   actual limit.
 * @returns Four formatted feature bullets.
 */
export function BuildPlanFeatureBullets(plan: IPlanDTO, effectiveCurrency: BillingCurrency, concurrentLimitOverride?: number): string[] {
  const meter = (key: string) => plan.meterPrices.find((entry) => entry.meterKey === key);
  const included = (key: string) => meter(key)?.includedQuantity ?? 0;
  const overage = (key: string) => {
    const entry = meter(key);
    if (!entry) return '';
    const amountMinor = ResolveCurrencyAmount(entry.unitAmount, entry.unitAmountByCurrency, plan.currency, effectiveCurrency);
    return FormatCurrencyMinor(amountMinor, effectiveCurrency);
  };
  return [
    FormatLocalized(Localized.LandingPlanIncludedCalls, String(included(CommonConstants.Database.MeterKey.AnsweredCalls)), overage(CommonConstants.Database.MeterKey.AnsweredCalls)),
    FormatLocalized(Localized.LandingPlanIncludedMinutes, String(included(CommonConstants.Database.MeterKey.VoiceMinutes)), overage(CommonConstants.Database.MeterKey.VoiceMinutes)),
    FormatLocalized(Localized.LandingPlanIncludedSMS, String(included(CommonConstants.Database.MeterKey.Messages)), overage(CommonConstants.Database.MeterKey.Messages)),
    FormatLocalized(Localized.LandingPlanConcurrentLines, String(concurrentLimitOverride ?? plan.concurrentLimit))
  ];
}

/** Props for a single plan card. */
interface IPlanCardProps {
  plan: IPlan;
  onChoose: () => void;
  // Pivot modifier driving the hover "screw inward" transform (top-left for the first column, top-right for the last, top-center otherwise).
  pivotClassName: string;
}

/**
 * A single pricing plan column.
 *
 * The whole panel is clickable and mirrors the panel's Get Started button; hovering screws the card inward about a corner pivot. The inner button stops propagation so a button click fires once.
 *
 * @param props - Component props.
 * @returns The plan card element.
 */
function PlanCard(props: IPlanCardProps) {
  const { plan } = props;
  // Whole-panel keyboard activation mirrors the Get Started button for pointer-free users.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== localConstants.keyEnter && event.key !== localConstants.keySpace) return;
    event.preventDefault();
    props.onChoose();
  };
  const baseClass = plan.featured ? `${Styles.plan} ${Styles.planFeatured}` : Styles.plan;
  const planPriceText = plan.setupFree
    ? FormatLocalized(Localized.LandingPricingPerMonthNoSetup, plan.currencyCode)
    : FormatLocalized(Localized.LandingPricingPerMonth, `${plan.currencySymbol}${plan.setupPrice}`, plan.currencyCode);
  const planPriceTitle = plan.free ? `${Localized.SharedFree} ${planPriceText}` : `${plan.currencySymbol}${plan.price} ${planPriceText}`;
  return (
    <div className={`${baseClass} ${props.pivotClassName}`} role="button" tabIndex={0} onClick={props.onChoose} onKeyDown={onKeyDown}>
      {plan.featured ? <Label text={Localized.LandingPlanProBadge} classNameContainer={Styles.planBadge} /> : null}
      <Label text={plan.name} classNameContainer={Styles.planName} />
      <Label text={plan.tagline} classNameContainer={Styles.planTagline} />
      <div className={Styles.planPrice}>
        {plan.free ? null : <Label title={planPriceTitle} text={plan.currencySymbol} classNameContainer={Styles.planCurrency} />}
        <Label title={planPriceTitle} text={plan.free ? Localized.SharedFree : plan.price} classNameContainer={Styles.planAmount} />
        <Label title={planPriceTitle} text={planPriceText} classNameContainer={Styles.planPeriod} />
      </div>
      <div className={Styles.planDivider} />
      <div className={Styles.planFeatures}>
        {plan.features.map((feature, position) => (
          <div key={position} className={Styles.planFeature}>
            {/* Raw <span>: decorative check glyph flagged aria-hidden; engine Label cannot set aria-hidden and would add a glyph title tooltip. */}
            <span className={Styles.planFeatureCheck} aria-hidden="true">
              {localConstants.check}
            </span>
            <Label text={feature} />
          </div>
        ))}
      </div>
      {/* Raw <span>: click shield stopping propagation to the clickable card around the engine Button; engine Label supports no event-arg onClick and renders text only. */}
      <span className={Styles.planBtnWrap} onClick={(event) => event.stopPropagation()}>
        <Button text={plan.cta} classNameButtonEnabled={plan.featured ? `${Styles.planBtn} ${Styles.planBtnFeatured}` : Styles.planBtn} onClickHandler={props.onChoose} />
      </span>
    </div>
  );
}

/**
 * Card 5 — pricing. Plans are fetched live from the billing catalog; the featured plan is highlighted.
 *
 * @param props - Component props.
 * @returns The pricing card element.
 */
export function PricingCard(props: IPricingCardProps) {
  const currency = props.currency;
  const plans = useMemo<IPlan[]>(
    () =>
      props.plans.map((plan) => {
        // The visitor currency only applies when the plan carries a value for it; otherwise the prices fall back to the plan's default currency (USD). Mirrors the checkout currency resolution.
        const effectiveCurrency = ResolveSupportedCurrency(plan.baseAmountByCurrency, plan.currency, currency);
        const baseAmountMinor = ResolveCurrencyAmount(plan.baseAmount, plan.baseAmountByCurrency, plan.currency, effectiveCurrency);
        const setupAmountMinor = ResolveCurrencyAmount(plan.setupAmount, plan.setupAmountByCurrency, plan.currency, effectiveCurrency);
        return {
          key: plan.code,
          name: plan.name,
          tagline: plan.tagline ?? '',
          currencySymbol: GetCurrencySymbol(effectiveCurrency),
          currencyCode: effectiveCurrency.toUpperCase(),
          price: String(baseAmountMinor / localConstants.minorUnitsPerMajor),
          free: baseAmountMinor === 0,
          setupPrice: String(setupAmountMinor / localConstants.minorUnitsPerMajor),
          setupFree: setupAmountMinor === 0,
          features: [...plan.features, ...BuildPlanFeatureBullets(plan, effectiveCurrency)],
          cta: Localized.LandingPlanCTA,
          featured: plan.featured
        };
      }),
    [props.plans, currency]
  );

  return (
    <section className={`${Styles.cardInner} ${Styles.cardPricing}`}>
      <div className={Styles.cardTag}>
        <Label text={Localized.LandingPricingLabel} classNameContainer={Styles.sectionLabel} />
        {/* Structural wrapper: a single Label cannot render the two-line styled headline, so a div is used and each line carries the tooltip. */}
        <div className={Styles.sectionTitle}>
          <Label text={Localized.LandingPricingTitle1} title={`${Localized.LandingPricingTitle1} ${Localized.LandingPricingTitle2}`} />
          <br />
          <Label text={Localized.LandingPricingTitle2} title={`${Localized.LandingPricingTitle1} ${Localized.LandingPricingTitle2}`} classNameContainer={Styles.accent} />
        </div>
      </div>
      <div className={Styles.cardBody}>
        <div className={Styles.plans}>
          {plans.map((plan, position) => {
            // First column screws about its top-left, last about its top-right, the rest about top-centre.
            const pivotClassName = position === localConstants.firstIndex ? Styles.planScrewLeft : position === plans.length - 1 ? Styles.planScrewRight : Styles.planScrewCenter;
            return <PlanCard key={plan.key} plan={plan} pivotClassName={pivotClassName} onChoose={() => props.onChoosePlan(plan.key)} />;
          })}
        </div>
        <div className={Styles.pricingNote}>
          <Label text={Localized.LandingPricingNote} />
        </div>
      </div>
    </section>
  );
}
